import type {
  ParentType as Parent_Type_Enum,
  ParentTypes,
} from '@risksmart-app/domain/src/types/consts/parent-type';
import type { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { useGetAssessmentResultsByParentId } from 'src/hooks/queries/assessment-result/useGetAssessmentResultsByParentId';

import { getFriendlyId } from '@/utils/friendlyId';

import type {
  AssessmentResultFields,
  AssessmentResultTypeValue,
} from './types';

type GetAssessmentResultsByParentIdData = NonNullable<
  ReturnType<typeof useGetAssessmentResultsByParentId>['data']
>;

export const getParentTitle = (record: AssessmentResultFields) => {
  switch (record.ResultType) {
    case 'document_assessment_result':
      return (
        (
          record as Partial<
            Omit<
              GetAssessmentResultsByParentIdData['document_assessment_result'][0],
              'files'
            >
          >
        ).parents?.[0]?.document?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'obligation_assessment_result':
      return (
        (
          record as Partial<
            Omit<
              GetAssessmentResultsByParentIdData['obligation_assessment_result'][0],
              'files'
            >
          >
        ).parents?.[0]?.obligation?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'risk_assessment_result':
      return (
        (
          record as Partial<
            Omit<
              GetAssessmentResultsByParentIdData['risk_assessment_result'][0],
              'files'
            >
          >
        ).parents?.[0]?.risk?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'test_result':
      return (
        (
          record as Partial<
            Omit<GetAssessmentResultsByParentIdData['test_result'][0], 'files'>
          >
        )?.parent?.Title ?? '-'
      );
  }
};

export const decorateWithControlType = (
  value: string,
  getControlTypeLabel: (
    controlType: Risk_Assessment_Result_Control_Type_Enum
  ) => string,
  // Only the discriminator is read for non-risk rows; the risk variant also
  // needs ControlType, so require it there rather than asserting it. This
  // replaces a cast-through-Partial plus a non-null assertion that would have
  // rendered "Risk - undefined" had the column ever been absent.
  record:
    | {
        ResultType: Exclude<
          AssessmentResultTypeValue,
          typeof ParentTypes.RiskAssessmentResult
        >;
      }
    | {
        ResultType: typeof ParentTypes.RiskAssessmentResult;
        ControlType: Risk_Assessment_Result_Control_Type_Enum;
      }
) => {
  if (record.ResultType === 'risk_assessment_result') {
    return `${value} - ${getControlTypeLabel(record.ControlType)}`;
  }

  return value;
};
